import { AvailablePaymentMethods } from "@stripe/stripe-js";
import { system, SystemProps } from "flicket-ui";
import styled from "styled-components";

type ButtonFunction = (
  props: { onClick: () => void; disabled?: boolean } & Omit<
    SystemProps,
    "color"
  >
) => JSX.Element;

const Button = styled.button`
  width: fit-content;
  height: 48px;
  line-height: 48px;
  background-color: #000;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 16px;
  color: #fff;
  font-weight: 500;
  font-size: 15px;
  flex-shrink: 0;
  min-width: 80px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  ${system}
`;

export const GooglePayButton: ButtonFunction = (props) => {
  return (
    <Button {...props}>
      <svg height="20" viewBox="0 0 45 16" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path
            d="M19.526 2.635v4.083h2.518c.6 0 1.096-.202 1.488-.605.403-.402.605-.882.605-1.437 0-.544-.202-1.018-.605-1.422-.392-.413-.888-.62-1.488-.62h-2.518zm0 5.52v4.736h-1.504V1.198h3.99c1.013 0 1.873.337 2.582 1.012.72.675 1.08 1.497 1.08 2.466 0 .991-.36 1.819-1.08 2.482-.697.665-1.559.996-2.583.996h-2.485v.001zm7.668 2.287c0 .392.166.718.499.98.332.26.722.391 1.168.391.633 0 1.196-.234 1.692-.701.497-.469.744-1.019.744-1.65-.469-.37-1.123-.555-1.962-.555-.61 0-1.12.148-1.528.442-.409.294-.613.657-.613 1.093m1.946-5.815c1.112 0 1.989.297 2.633.89.642.594.964 1.408.964 2.442v4.932h-1.439v-1.11h-.065c-.622.914-1.45 1.372-2.486 1.372-.882 0-1.621-.262-2.215-.784-.594-.523-.891-1.176-.891-1.96 0-.828.313-1.486.94-1.976s1.463-.735 2.51-.735c.892 0 1.629.163 2.206.49v-.344c0-.522-.207-.966-.621-1.33a2.132 2.132 0 0 0-1.455-.547c-.84 0-1.504.353-1.995 1.062l-1.324-.834c.73-1.045 1.81-1.568 3.238-1.568m11.853.262l-5.02 11.53H34.42l1.864-4.034-3.302-7.496h1.635l2.387 5.749h.032l2.322-5.75z"
            fill="#FFF"
          />
          <path
            d="M13.448 7.134c0-.473-.04-.93-.116-1.366H6.988v2.588h3.634a3.11 3.11 0 0 1-1.344 2.042v1.68h2.169c1.27-1.17 2.001-2.9 2.001-4.944"
            fill="#4285F4"
          />
          <path
            d="M6.988 13.7c1.816 0 3.344-.595 4.459-1.621l-2.169-1.681c-.603.406-1.38.643-2.29.643-1.754 0-3.244-1.182-3.776-2.774H.978v1.731a6.728 6.728 0 0 0 6.01 3.703"
            fill="#34A853"
          />
          <path
            d="M3.212 8.267a4.034 4.034 0 0 1 0-2.572V3.964H.978A6.678 6.678 0 0 0 .261 6.98c0 1.085.26 2.11.717 3.017l2.234-1.731z"
            fill="#FABB05"
          />
          <path
            d="M6.988 2.921c.992 0 1.88.34 2.58 1.008v.001l1.92-1.918C10.324.928 8.804.262 6.989.262a6.728 6.728 0 0 0-6.01 3.702l2.234 1.731c.532-1.592 2.022-2.774 3.776-2.774"
            fill="#E94235"
          />
        </g>
      </svg>
    </Button>
  );
};

export const ApplePayButton: ButtonFunction = (props) => {
  return (
    <Button {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="18"
        focusable="false"
        viewBox="0 0 39 16"
        fill="none"
      >
        <path
          d="M7.12604 2.05766C6.66895 2.59854 5.93762 3.02515 5.20629 2.96421C5.11487 2.23287 5.47292 1.45583 5.89191 0.975894C6.349 0.419776 7.14889 0.0236363 7.79643 -0.00683594C7.87261 0.75497 7.5755 1.50154 7.12604 2.05766ZM7.78881 3.10895C6.7299 3.04801 5.82335 3.71078 5.32056 3.71078C4.81015 3.71078 4.04072 3.13942 3.20274 3.15466C2.11335 3.1699 1.10015 3.78696 0.544032 4.76969C-0.598677 6.73515 0.246928 9.64525 1.35155 11.245C1.89243 12.0373 2.53996 12.9058 3.39319 12.8753C4.2007 12.8448 4.52066 12.3497 5.49577 12.3497C6.4785 12.3497 6.76037 12.8753 7.61359 12.8601C8.49729 12.8448 9.05341 12.0678 9.59429 11.2755C10.2114 10.3766 10.4627 9.50051 10.478 9.4548C10.4627 9.43956 8.77154 8.79203 8.7563 6.8418C8.74107 5.21154 10.0895 4.43449 10.1504 4.38879C9.3886 3.26131 8.20018 3.13942 7.78881 3.10895ZM13.9061 0.899713V12.7763H15.7497V8.71585H18.3017C20.6329 8.71585 22.2707 7.11605 22.2707 4.80016C22.2707 2.48427 20.6633 0.899713 18.3627 0.899713H13.9061ZM15.7497 2.4538H17.8751C19.4749 2.4538 20.3891 3.30702 20.3891 4.80778C20.3891 6.30854 19.4749 7.16938 17.8675 7.16938H15.7497V2.4538ZM25.6379 12.8677C26.7959 12.8677 27.87 12.2811 28.3576 11.3517H28.3957V12.7763H30.1021V6.86466C30.1021 5.15059 28.7309 4.04597 26.6207 4.04597C24.6628 4.04597 23.2154 5.16583 23.1621 6.70468H24.8228C24.9599 5.97334 25.6379 5.4934 26.5673 5.4934C27.6948 5.4934 28.3271 6.01905 28.3271 6.98655V7.6417L26.0265 7.77882C23.8858 7.90833 22.7278 8.78441 22.7278 10.308C22.7278 11.8469 23.9239 12.8677 25.6379 12.8677ZM26.1331 11.4583C25.1504 11.4583 24.5257 10.986 24.5257 10.2623C24.5257 9.51574 25.1275 9.08151 26.2778 9.01295L28.3271 8.88344V9.55383C28.3271 10.6661 27.3825 11.4583 26.1331 11.4583ZM32.3799 16.0063C34.1778 16.0063 35.0234 15.3207 35.7623 13.241L39 4.16024H37.126L34.9548 11.1765H34.9167L32.7456 4.16024H30.8182L33.9416 12.8067L33.774 13.3324C33.4921 14.2237 33.0351 14.5665 32.2199 14.5665C32.0752 14.5665 31.7933 14.5513 31.6791 14.536V15.9606C31.7857 15.9911 32.2428 16.0063 32.3799 16.0063Z"
          fill="white"
        />
      </svg>
    </Button>
  );
};

export const LinkButton: ButtonFunction = (props) => {
  return (
    <Button
      css={{
        backgroundColor: "#00d66f",
      }}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="18"
        focusable="false"
        viewBox="0 0 72 24"
        fill="none"
      >
        <path
          fill="#011E0F"
          d="M36.12 3.677c0-1.128.95-2.045 2.069-2.045 1.118 0 2.069.922 2.069 2.045a2.075 2.075 0 0 1-2.07 2.069 2.057 2.057 0 0 1-2.068-2.07ZM29.98 1.92h3.6v20.16h-3.6V1.92ZM40.008 7.68h-3.629v14.4h3.629V7.68ZM66.096 14.39c2.731-1.68 4.589-4.18 5.323-6.715H67.79c-.945 2.42-3.115 4.239-5.5 5.011V1.916h-3.63v20.16h3.63V16.08c2.77.691 4.958 3.086 5.707 5.995h3.653c-.557-3.053-2.645-5.909-5.554-7.685ZM46.44 9.293c.95-1.263 2.803-1.997 4.306-1.997 2.803 0 5.121 2.05 5.126 5.146v9.633h-3.629v-8.832c0-1.272-.566-2.74-2.405-2.74-2.16 0-3.403 1.915-3.403 4.156v7.426h-3.629V7.69h3.634v1.603ZM12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12Z"
        ></path>
        <path
          fill="#fff"
          d="M11.448 4.8h-3.7c.72 3.01 2.821 5.582 5.452 7.2-2.635 1.618-4.733 4.19-5.453 7.2h3.7c.918-2.784 3.457-5.203 6.577-5.698v-3.01c-3.125-.489-5.664-2.908-6.576-5.692Z"
        ></path>
      </svg>
    </Button>
  );
};

export const KlarnaButton: ButtonFunction = (props) => {
  return (
    <Button css={{}} {...props}>
      <svg
        height="30"
        viewBox="0 0 64 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Klarna badge"
      >
        <path
          d="M0.5 14C0.5 8.38215 0.5 5.57323 1.84824 3.55544C2.43191 2.68192 3.18192 1.93191 4.05544 1.34824C6.07323 0 8.88215 0 14.5 0H50.5C56.1178 0 58.9268 0 60.9446 1.34824C61.8181 1.93191 62.5681 2.68192 63.1518 3.55544C64.5 5.57323 64.5 8.38215 64.5 14C64.5 19.6178 64.5 22.4268 63.1518 24.4446C62.5681 25.3181 61.8181 26.0681 60.9446 26.6518C58.9268 28 56.1178 28 50.5 28H14.5C8.88215 28 6.07323 28 4.05544 26.6518C3.18192 26.0681 2.43191 25.3181 1.84824 24.4446C0.5 22.4268 0.5 19.6178 0.5 14Z"
          fill="#FFA8CD"
        ></path>
        <path
          d="M52.3359 18.0391C53.4609 18.0391 54.3438 17.1016 54.3438 15.9688C54.3438 14.8359 53.4609 13.9062 52.3359 13.9062C51.2031 13.9062 50.3281 14.8359 50.3281 15.9688C50.3281 17.1016 51.2031 18.0391 52.3359 18.0391ZM8.32812 20V8.34375H10.9688V14.1562L11.7266 13.625C13.6719 12.2891 14.9453 10.4219 14.9453 8.34375H17.4844C17.5 10.9453 16.2578 13.0938 14.3516 14.4766L18.5469 20H15.3281L10.9688 14.2266V20H8.32812ZM19.3125 20V8.34375H21.7969V20H19.3125ZM22.7734 15.9688C22.7734 13.5547 24.3984 11.7266 26.7031 11.7266C27.6719 11.7266 28.8984 12.0938 29.5781 13.5234L29.6406 13.4922C29.3438 12.7109 29.3438 12.2422 29.3438 12.125V11.9375H31.7656V20H29.3438V19.8203C29.3438 19.7031 29.3438 19.2344 29.6406 18.4531L29.5781 18.4219C28.8984 19.8516 27.6719 20.2188 26.7031 20.2188C24.3984 20.2188 22.7734 18.3828 22.7734 15.9688ZM25.2656 15.9688C25.2656 17.1016 26.1406 18.0391 27.2734 18.0391C28.3984 18.0391 29.2812 17.1016 29.2812 15.9688C29.2812 14.8359 28.3984 13.9062 27.2734 13.9062C26.1406 13.9062 25.2656 14.8359 25.2656 15.9688ZM32.9922 20V11.9375H35.4141V12.125C35.4141 12.2422 35.4141 12.7109 35.1172 13.4922L35.1797 13.5234C35.7266 12.125 36.7109 11.7109 37.8047 11.9375V14.4062C37.5547 14.3359 37.3359 14.3047 37.0703 14.3047C36.0938 14.3047 35.5 14.9922 35.5 16.125L35.4844 20H32.9922ZM38.7812 20V11.9375H41.2031V12.125C41.2031 12.2422 41.2031 12.7109 40.9062 13.4922L40.9688 13.5234C41.6719 12.1094 42.5312 11.7266 43.625 11.7266C45.5859 11.7266 47.0078 13.0391 47.0078 14.875V20H44.5234V15.7422C44.5234 14.5391 44.0078 13.9375 42.9609 13.9375C41.9141 13.9375 41.2734 14.6406 41.2734 15.7578V20H38.7812ZM47.8359 15.9688C47.8359 13.5547 49.4609 11.7266 51.7734 11.7266C52.7344 11.7266 53.9609 12.0938 54.6406 13.5234L54.7031 13.4922C54.4062 12.7109 54.4062 12.2422 54.4062 12.125V11.9375H56.8281V20H54.4062V19.8203C54.4062 19.7031 54.4062 19.2344 54.7031 18.4531L54.6406 18.4219C53.9609 19.8516 52.7344 20.2188 51.7734 20.2188C49.4609 20.2188 47.8359 18.3828 47.8359 15.9688Z"
          fill="#0B051D"
        ></path>
      </svg>
    </Button>
  );
};

export const PaypalButton: ButtonFunction = (props) => {
  return (
    <Button
      css={{
        backgroundColor: "#ffc439",
      }}
      {...props}
    >
      <svg viewBox="0 0 101 32" height="20" xmlns="http://www.w3.org/2000/svg">
        <path
          fill="#003087"
          d="M12.24 2.8h-7.8c-.5 0-1 .4-1.1.9l-3.1 20c-.1.4.2.7.6.7h3.7c.5 0 1-.4 1.1-.9l.8-5.4c.1-.5.5-.9 1.1-.9h2.5c5.1 0 8.1-2.5 8.9-7.4.3-2.1 0-3.8-1-5-1.1-1.3-3.1-2-5.7-2Zm.9 7.3c-.4 2.8-2.6 2.8-4.6 2.8h-1.2l.8-5.2c0-.3.3-.5.6-.5h.5c1.4 0 2.7 0 3.4.8.5.4.7 1.1.5 2.1Zm22.3-.1h-3.7c-.3 0-.6.2-.6.5l-.2 1-.3-.4c-.8-1.2-2.6-1.6-4.4-1.6-4.1 0-7.6 3.1-8.3 7.5-.4 2.2.1 4.3 1.4 5.7 1.1 1.3 2.8 1.9 4.7 1.9 3.3 0 5.2-2.1 5.2-2.1l-.2 1c-.1.4.2.8.6.8h3.4c.5 0 1-.4 1.1-.9l2-12.8c.1-.2-.3-.6-.7-.6Zm-5.1 7.2c-.4 2.1-2 3.6-4.2 3.6-1.1 0-1.9-.3-2.5-1-.6-.7-.8-1.6-.6-2.6a4.2 4.2 0 0 1 4.2-3.6c1.1 0 1.9.4 2.5 1 .5.7.7 1.6.6 2.6Zm25-7.2h-3.7c-.4 0-.7.2-.9.5l-5.2 7.6-2.2-7.3c-.1-.5-.6-.8-1-.8h-3.7c-.4 0-.8.4-.6.9l4.1 12.1-3.9 5.4c-.3.4 0 1 .5 1h3.7c.4 0 .7-.2.9-.5l12.5-18c.3-.3 0-.9-.5-.9Z"
        ></path>
        <path
          fill="#009cde"
          d="M67.74 2.8h-7.8c-.5 0-1 .4-1.1.9l-3.1 19.9c-.1.4.2.7.6.7h4c.4 0 .7-.3.7-.6l.9-5.7c.1-.5.5-.9 1.1-.9h2.5c5.1 0 8.1-2.5 8.9-7.4.3-2.1 0-3.8-1-5-1.2-1.2-3.1-1.9-5.7-1.9Zm.9 7.3c-.4 2.8-2.6 2.8-4.6 2.8h-1.2l.8-5.2c0-.3.3-.5.6-.5h.5c1.4 0 2.7 0 3.4.8.5.4.6 1.1.5 2.1Zm22.3-.1h-3.7c-.3 0-.6.2-.6.5l-.2 1-.3-.4c-.8-1.2-2.6-1.6-4.4-1.6-4.1 0-7.6 3.1-8.3 7.5-.4 2.2.1 4.3 1.4 5.7 1.1 1.3 2.8 1.9 4.7 1.9 3.3 0 5.2-2.1 5.2-2.1l-.2 1c-.1.4.2.8.6.8h3.4c.5 0 1-.4 1.1-.9l2-12.8c0-.2-.3-.6-.7-.6Zm-5.2 7.2c-.4 2.1-2 3.6-4.2 3.6-1.1 0-1.9-.3-2.5-1-.6-.7-.8-1.6-.6-2.6a4.2 4.2 0 0 1 4.2-3.6c1.1 0 1.9.4 2.5 1 .6.7.8 1.6.6 2.6Zm9.6-13.9-3.2 20.3c-.1.4.2.7.6.7h3.2c.5 0 1-.4 1.1-.9l3.2-19.9c.1-.4-.2-.7-.6-.7h-3.6c-.4 0-.6.2-.7.5Z"
        ></path>
      </svg>
    </Button>
  );
};

export const AmazonPayButton: ButtonFunction = (props) => {
  return (
    <Button
      css={{
        backgroundColor: "#ffd814",
        backgroundImage:
          "url(https://m.media-amazon.com/images/G/02/AmazonPay/Maxo/amazonpay-logo-rgb_drk_1.svg)",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "auto 25px",
      }}
      {...props}
    ></Button>
  );
};

export const ButtonMap: Record<
  keyof AvailablePaymentMethods,
  ButtonFunction
> = {
  applePay: ApplePayButton,
  googlePay: GooglePayButton,
  amazonPay: AmazonPayButton,
  link: LinkButton,
  paypal: PaypalButton,
  klarna: KlarnaButton,
};
